import { useEffect, useState } from "react";
import { useFirstMountState } from "react-use";
import { RouteFactory } from "@app/routePaths";
import { useHookUpdatePulseUrl } from "@features/pulse/hooks/useHookUpdatePulseUrl";
import useSendPulseGaData from "@features/pulse/hooks/useSendPulseGaData";
import {
  PulseArticleObject,
  SanitizedPulseData,
} from "@features/pulse/utils/types";
import useSendChartbeatViritualPageData from "@hooks/useSendChartbeatViritualPageData";
import { LIVE_DOMAIN } from "@util/constant";

/**
 * This hook sets the sorted pulse articles and individual pulse articles.
 * It sorts the articles based on the provided pulseArticles,
 * and updates the URL to reflect the first article's ID.
 */
export const useSetPulseArticle = ({ pulseArticles }: PulseArticleObject) => {
  const [sortedPulseArticles, setSortedPulseArticles] = useState<
    SanitizedPulseData[]
  >([]);

  const { updatePulseUrl } = useHookUpdatePulseUrl();
  const { sendTrackingData } = useSendPulseGaData();

  const { sendChartbeatData } = useSendChartbeatViritualPageData();
  const isFirstMount = useFirstMountState();

  useEffect(() => {
    if (pulseArticles.length === 0) return;
    if (!isFirstMount) return;

    setSortedPulseArticles(pulseArticles);
    updatePulseUrl(pulseArticles[0].id, true);

    const firstArticle = pulseArticles?.[0];

    const { title, id: activeId, sections } = firstArticle;
    const virtualUrl = RouteFactory.pulseSlide(activeId);

    const pagination = `1/${pulseArticles.length}`;
    sendTrackingData(title, virtualUrl, activeId, pulseArticles, pagination);

    sendChartbeatData({
      url: `${LIVE_DOMAIN}${virtualUrl}`,
      title,
      sections: sections,
    });
  }, [
    isFirstMount,
    pulseArticles,
    updatePulseUrl,
    sendTrackingData,
    sendChartbeatData,
  ]);

  return { sortedPulseArticles };
};
